import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useLang } from "../contexts/LanguageContext";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { api } from "../lib/api";
import { toast } from "sonner";
import confetti from "canvas-confetti";
import {
  GraduationCap,
  Sparkles,
  Check,
  X,
  ArrowRight,
  ArrowLeft,
  Trophy,
  RotateCcw,
} from "lucide-react";
import SpeakButton from "../components/SpeakButton";

// 7 questions covering the essentials — plain language, 3 options each.
const QUESTIONS = {
  fr: [
    {
      q: "Que veut dire « intérêts composés » ?",
      choices: [
        "On paie des intérêts à sa banque chaque mois.",
        "Les intérêts gagnés produisent eux aussi des intérêts l'année suivante.",
        "C'est un impôt sur l'épargne.",
      ],
      correct: 1,
      explain:
        "Exactement. Chaque année, vos intérêts précédents commencent à leur tour à rapporter. C'est ce qui fait grimper la courbe.",
    },
    {
      q: "Quel est le montant minimum pour ouvrir un Livret A ?",
      choices: ["1 000 €", "10 €", "500 €"],
      correct: 1,
      explain:
        "10 € suffisent. Le Livret A est fait pour tout le monde, sans condition de revenus.",
    },
    {
      q: "Un conseiller vous appelle et demande vos codes bancaires. Que faites-vous ?",
      choices: [
        "Vous donnez, c'est votre conseiller.",
        "Vous raccrochez et rappelez votre banque avec le numéro habituel.",
        "Vous demandez son nom et donnez ensuite.",
      ],
      correct: 1,
      explain:
        "Une vraie banque ne demande JAMAIS vos codes au téléphone. Toujours raccrocher et rappeler avec un numéro connu.",
    },
    {
      q: "Sur combien d'années faut-il idéalement placer son argent en Bourse ?",
      choices: [
        "6 mois, pour bien profiter",
        "Au moins 8 à 10 ans",
        "Peu importe, on peut retirer quand on veut sans risque",
      ],
      correct: 1,
      explain:
        "8 à 10 ans, c'est la durée qui laisse aux marchés le temps d'absorber les creux. À court terme, la Bourse peut baisser.",
    },
    {
      q: "Qu'est-ce que la « diversification » ?",
      choices: [
        "Mettre tout son argent sur le même placement pour bien suivre.",
        "Répartir son argent sur plusieurs placements pour réduire le risque.",
        "Changer de banque chaque année.",
      ],
      correct: 1,
      explain:
        "Bien vu ! Ne pas mettre tous ses œufs dans le même panier : si un placement baisse, les autres compensent.",
    },
    {
      q: "Un ETF, c'est quoi en une phrase ?",
      choices: [
        "Un livret d'épargne comme le Livret A.",
        "Un panier d'actions ou d'obligations qu'on achète en une seule fois.",
        "Une carte bancaire spéciale pour investir.",
      ],
      correct: 1,
      explain:
        "Un ETF, c'est un panier tout prêt. On achète une part, et on possède un petit bout de nombreuses entreprises à la fois.",
    },
    {
      q: "Vaut-il mieux commencer à investir avec 500 € d'un coup ou 50 € par mois ?",
      choices: [
        "500 € d'un coup, forcément mieux.",
        "50 € par mois, ça lisse les hauts et les bas.",
        "Aucun des deux, il faut au moins 5 000 €.",
      ],
      correct: 1,
      explain:
        "50 € réguliers, c'est mieux : vous achetez à différents prix et vous prenez l'habitude. On appelle ça le « versement programmé ».",
    },
  ],
  en: [
    {
      q: "What does \"compound interest\" mean?",
      choices: [
        "You pay interest to your bank every month.",
        "The interest you earned starts earning interest itself the following year.",
        "It's a tax on savings.",
      ],
      correct: 1,
      explain:
        "Exactly. Every year, your past interest also starts to earn interest. That's what makes the curve rise faster.",
    },
    {
      q: "What's the minimum amount to open a Livret A savings account?",
      choices: ["€1,000", "€10", "€500"],
      correct: 1,
      explain:
        "€10 is enough. The Livret A is for everyone, no income requirement.",
    },
    {
      q: "An advisor calls you and asks for your banking codes. What do you do?",
      choices: [
        "You give them, it's your advisor.",
        "You hang up and call your bank back with the usual number.",
        "You ask for their name then give the codes.",
      ],
      correct: 1,
      explain:
        "A real bank NEVER asks for your codes over the phone. Always hang up and call back with a known number.",
    },
    {
      q: "How many years should you ideally invest in stocks for?",
      choices: [
        "6 months, to enjoy it fully",
        "At least 8 to 10 years",
        "Doesn't matter, you can withdraw anytime risk-free",
      ],
      correct: 1,
      explain:
        "8 to 10 years lets markets absorb the dips. Short term, stocks can go down.",
    },
    {
      q: "What is \"diversification\"?",
      choices: [
        "Putting all your money in the same investment to track it well.",
        "Spreading your money across several investments to reduce risk.",
        "Changing banks every year.",
      ],
      correct: 1,
      explain:
        "Well spotted! Don't put all your eggs in one basket: if one investment drops, the others make up for it.",
    },
    {
      q: "What's an ETF in one sentence?",
      choices: [
        "A savings account like the Livret A.",
        "A basket of stocks or bonds bought in one go.",
        "A special bank card to invest.",
      ],
      correct: 1,
      explain:
        "An ETF is a ready-made basket. You buy one share, and you own a tiny piece of many companies at once.",
    },
    {
      q: "Is it better to start investing with €500 in one go or €50 per month?",
      choices: [
        "€500 in one go, obviously better.",
        "€50 per month smooths out the highs and lows.",
        "Neither, you need at least €5,000.",
      ],
      correct: 1,
      explain:
        "Regular €50 is better: you buy at different prices and build the habit. It's called \"scheduled contributions\".",
    },
  ],
};

const T = {
  fr: {
    title: "Quiz financier",
    subtitle: "7 petites questions pour vérifier ce que vous avez retenu. Zéro pression.",
    question_prefix: "Question",
    of: "sur",
    next: "Suivant",
    finish: "Voir mon résultat",
    correct: "Bonne réponse !",
    wrong: "La bonne réponse était :",
    result_title: "Résultat",
    excellent: "Bravo, vous êtes une vraie experte en herbe !",
    good: "Bon score, vous avez retenu l'essentiel.",
    keep_going: "Continuez, chaque question apprise vaut de l'or.",
    badge_unlocked: "Badge « Expert en herbe » débloqué !",
    replay: "Refaire le quiz",
    to_dashboard: "Retour au tableau de bord",
    listen: "Écouter la question",
  },
  en: {
    title: "Financial quiz",
    subtitle: "7 short questions to check what you've learned. No pressure.",
    question_prefix: "Question",
    of: "of",
    next: "Next",
    finish: "See my result",
    correct: "Correct!",
    wrong: "The right answer was:",
    result_title: "Result",
    excellent: "Bravo, you're a true budding expert!",
    good: "Nice score, you've got the essentials.",
    keep_going: "Keep going — every question learned is worth gold.",
    badge_unlocked: "\"Budding Expert\" badge unlocked!",
    replay: "Play again",
    to_dashboard: "Back to dashboard",
    listen: "Listen to the question",
  },
};

export default function Quiz() {
  const { lang } = useLang();
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const tr = T[lang] || T.fr;
  const questions = QUESTIONS[lang] || QUESTIONS.fr;

  const [step, setStep] = useState(0);
  const [selected, setSelected] = useState(null);
  const [answered, setAnswered] = useState(false);
  const [score, setScore] = useState(0);
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [gotBadge, setGotBadge] = useState(false);

  const total = questions.length;
  const q = questions[step];
  const progress = ((step + (answered ? 1 : 0)) / total) * 100;

  const choose = (i) => {
    if (answered) return;
    setSelected(i);
    setAnswered(true);
    if (i === q.correct) setScore((s) => s + 1);
  };

  const goNext = async () => {
    if (step < total - 1) {
      setStep((s) => s + 1);
      setSelected(null);
      setAnswered(false);
      return;
    }
    // Finished — submit
    setSubmitting(true);
    try {
      const { data } = await api.post("/quiz/complete", {
        score,
        total,
      });
      const newBadges = data.new_badges || [];
      if (newBadges.includes("expert_en_herbe")) {
        setGotBadge(true);
        celebrate();
        toast.success(tr.badge_unlocked, { duration: 6000 });
      }
      await refresh();
    } catch {
      // ignore silently — quiz completion is client-side visible anyway
    } finally {
      setSubmitting(false);
      setDone(true);
    }
  };

  const restart = () => {
    setStep(0);
    setSelected(null);
    setAnswered(false);
    setScore(0);
    setDone(false);
    setGotBadge(false);
  };

  // Result view
  if (done) {
    const pct = Math.round((score / total) * 100);
    const message =
      pct >= 85 ? tr.excellent : pct >= 60 ? tr.good : tr.keep_going;
    return (
      <div
        className="mx-auto max-w-2xl px-6 py-16 text-center"
        data-testid="quiz-result"
      >
        <div
          className={`mx-auto mb-6 flex h-24 w-24 items-center justify-center rounded-full shadow-[0_10px_30px_rgba(212,160,23,0.35)] ${
            gotBadge
              ? "bg-gradient-to-br from-[#FFF3C4] to-[#E59D32] text-[#5B4200]"
              : "bg-[#101A42] text-white"
          }`}
        >
          {gotBadge ? (
            <Trophy className="h-12 w-12" strokeWidth={2} />
          ) : (
            <GraduationCap className="h-12 w-12" strokeWidth={2} />
          )}
        </div>
        <h1 className="font-serif text-4xl font-semibold text-[#101A42] sm:text-5xl">
          {tr.result_title} : {score}/{total}
        </h1>
        <p
          className={`mt-4 text-2xl font-semibold ${
            gotBadge ? "text-[#B8860B]" : "text-[#101A42]"
          }`}
          data-testid="quiz-score-pct"
        >
          {pct} %
        </p>
        <p className="mt-6 text-lg leading-relaxed text-[#4A4A4A]">{message}</p>

        {gotBadge && (
          <div
            className="mx-auto mt-8 max-w-md rounded-2xl border-2 border-[#E59D32] bg-gradient-to-br from-[#FFF7E9] to-[#FFF3C4] p-5 shadow-[0_10px_30px_rgba(212,160,23,0.15)]"
            data-testid="quiz-badge-card"
          >
            <div className="flex items-center gap-3">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#E59D32]/25 text-[#8A6300]">
                <Sparkles className="h-7 w-7" strokeWidth={2} />
              </div>
              <div className="text-left">
                <p className="text-sm font-semibold uppercase tracking-widest text-[#8A6300]">
                  {tr.badge_unlocked}
                </p>
                <p className="font-serif text-lg font-semibold text-[#5B4200]">
                  Expert en herbe
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
          <Button
            onClick={restart}
            variant="outline"
            data-testid="quiz-replay-btn"
            className="min-h-[56px] rounded-full border-2 border-[#101A42] px-6 text-lg font-semibold text-[#101A42] hover:bg-[#101A42] hover:text-white"
          >
            <RotateCcw className="mr-2 h-5 w-5" />
            {tr.replay}
          </Button>
          <Button
            onClick={() => navigate("/dashboard")}
            data-testid="quiz-dashboard-btn"
            className="min-h-[56px] rounded-full bg-[#101A42] px-6 text-lg font-semibold text-white hover:bg-[#1A2650]"
          >
            {tr.to_dashboard}
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </div>
    );
  }

  // Question view
  return (
    <div className="mx-auto max-w-2xl px-6 py-12" data-testid="quiz-page">
      <div className="mb-6 flex items-center gap-3">
        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#A762B1]/15 text-[#A762B1]">
          <GraduationCap className="h-6 w-6" strokeWidth={2} />
        </div>
        <div>
          <p className="text-sm font-semibold uppercase tracking-widest text-[#A762B1]">
            {tr.title}
          </p>
          <p className="text-base text-[#4A4A4A]">{tr.subtitle}</p>
        </div>
      </div>

      <div className="mb-2 flex items-center justify-between text-sm text-[#4A4A4A]">
        <span data-testid="quiz-progress-label">
          {tr.question_prefix}{" "}
          <strong className="text-[#101A42]">{step + 1}</strong> {tr.of} {total}
        </span>
        <span>{Math.round(progress)} %</span>
      </div>
      <Progress
        value={progress}
        className="h-2"
        data-testid="quiz-progress"
      />

      <div
        className="mt-6 rounded-3xl border-2 border-[#E8DFC7] bg-white p-6 sm:p-8"
        data-testid={`quiz-question-${step + 1}`}
      >
        <div className="mb-4 flex items-start justify-between gap-3">
          <h2 className="font-serif text-2xl font-semibold text-[#101A42] sm:text-3xl">
            {q.q}
          </h2>
          <SpeakButton text={q.q} testid="quiz-question-speak" />
        </div>

        <div className="space-y-3">
          {q.choices.map((c, i) => {
            const isSelected = selected === i;
            const isCorrect = i === q.correct;
            let cls = "border-[#101A42]/20 bg-white hover:border-[#101A42]";
            let icon = null;
            if (answered) {
              if (isCorrect) {
                cls = "border-[#A762B1] bg-[#A762B1]/10 text-[#101A42]";
                icon = <Check className="h-5 w-5 text-[#A762B1]" />;
              } else if (isSelected) {
                cls = "border-[#A762B1] bg-[#A762B1]/10 text-[#6E3978]";
                icon = <X className="h-5 w-5 text-[#A762B1]" />;
              } else {
                cls = "border-[#E8DFC7] bg-[#FFF7E9] text-[#8A8A8A]";
              }
            }
            return (
              <button
                key={i}
                type="button"
                onClick={() => choose(i)}
                disabled={answered}
                data-testid={`quiz-choice-${i}`}
                className={`flex w-full items-center justify-between gap-3 rounded-2xl border-2 px-5 py-4 text-left text-lg transition-colors ${cls}`}
              >
                <span className="flex-1">{c}</span>
                {icon}
              </button>
            );
          })}
        </div>

        {answered && (
          <div
            data-testid="quiz-explain"
            className={`mt-5 rounded-2xl border-2 p-4 ${
              selected === q.correct
                ? "border-[#A762B1]/40 bg-[#A762B1]/5"
                : "border-[#A762B1]/40 bg-[#A762B1]/5"
            }`}
          >
            <p
              className={`mb-1 text-sm font-semibold uppercase tracking-widest ${
                selected === q.correct ? "text-[#A762B1]" : "text-[#A762B1]"
              }`}
            >
              {selected === q.correct ? tr.correct : tr.wrong + " " + q.choices[q.correct]}
            </p>
            <p className="text-base leading-relaxed text-[#101A42]">
              {q.explain}
            </p>
          </div>
        )}
      </div>

      <div className="mt-6 flex justify-end">
        <Button
          onClick={goNext}
          disabled={!answered || submitting}
          data-testid="quiz-next-btn"
          className="min-h-[56px] rounded-full bg-[#A762B1] px-6 text-lg font-semibold text-white hover:bg-[#B775C0] disabled:opacity-50"
        >
          {step < total - 1 ? tr.next : tr.finish}
          <ArrowRight className="ml-2 h-5 w-5" />
        </Button>
      </div>
    </div>
  );
}

// Gold + mauve celebration burst
function celebrate() {
  const colors = ["#E59D32", "#F4C77B", "#A762B1", "#A78BFA", "#FFE68C"];
  const fire = () => {
    confetti({
      particleCount: 100,
      spread: 80,
      startVelocity: 45,
      origin: { x: 0.5, y: 0.7 },
      colors,
      scalar: 1.2,
    });
  };
  fire();
  setTimeout(fire, 300);
  setTimeout(fire, 600);
}
